import { useState } from "react";
import {
	clearDevice,
	deviceBackup,
	parseDeviceBackup,
	restoreDeviceBackup,
	shrinkTripPhotos,
	type DeviceBackup,
} from "../storage/backup";
import { downloadTripFile } from "./TripTransfer";
export function DeviceDataPanel({
	onChanged,
}: {
	onChanged?: () => Promise<void>;
}) {
	const [photos, setPhotos] = useState(false),
		[preview, setPreview] = useState<DeviceBackup | null>(null),
		[mode, setMode] = useState<"both" | "replace">("both"),
		[confirm, setConfirm] = useState(""),
		[notice, setNotice] = useState(""),
		[busy, setBusy] = useState(false),
		[storage, setStorage] = useState("");
	async function action(fn: () => Promise<string>) {
		setBusy(true);
		try {
			setNotice(await fn());
			await onChanged?.();
		} catch {
			setNotice(
				"저장소 작업을 마치지 못했어요. 일부 기록만 반영됐을 수 있어요. 백업 파일을 보관하고 저장 공간을 확인한 뒤 다시 시도해 주세요.",
			);
		} finally {
			setBusy(false);
		}
	}
	return (
		<details className="workspace-disclosure device-data">
			<summary>기기 바꿀 때 · 전체 백업·복원·저장 공간</summary>
			<p>
				여행·도착 카드·모집문·설정을 한 파일로 옮겨요. 식사 카드도 파일에
				들어가요. 파일을 가진 사람은 내용을 볼 수 있어요.
			</p>
			<label>
				<input
					type="checkbox"
					checked={photos}
					onChange={(e) => setPhotos(e.target.checked)}
				/>
				내 사진도 포함
			</label>
			<button
				type="button"
				className="button secondary"
				disabled={busy}
				onClick={() =>
					void action(async () => {
						const text = JSON.stringify(await deviceBackup(photos));
						if (new Blob([text]).size > 20 * 1024 * 1024)
							return "20MB를 넘어요. 사진을 제외하고 다시 받아 주세요.";
						downloadTripFile("일본골목-전체백업.json", text);
						return "전체 백업 파일을 만들었어요. 안전한 곳에 보관해 주세요.";
					})
				}
			>
				전체 백업 받기
			</button>
			<label>
				백업 파일로 복원
				<input
					disabled={busy}
					type="file"
					accept=".json,application/json"
					onChange={async (e) => {
						const f = e.target.files?.[0];
						if (!f) return;
						try {
							if (f.size > 20 * 1024 * 1024) throw new Error("SIZE");
							const b = parseDeviceBackup(await f.text());
							setPreview(b);
							setNotice("복원 전에 개수를 확인해 주세요.");
						} catch {
							setPreview(null);
							setNotice(
								"백업 파일을 읽지 못했어요. 일본골목 전체 백업 파일인지와 20MB 이하인지 확인해 주세요.",
							);
						}
					}}
				/>
			</label>
			{preview && (
				<>
					<ul>
						{Object.entries(preview.stores).map(([name, rows]) => (
							<li key={name}>
								{
									(
										{
											trips: "여행",
											"arrival-cards": "도착 카드",
											"companion-plans": "모집문",
											profile: "설정",
											"saved-walks": "찜 코스",
											trash: "지운 여행",
										} as Record<string, string>
									)[name]
								}{" "}
								{rows.length}개
							</li>
						))}
					</ul>
					<label>
						같은 기록이 있으면
						<select
							value={mode}
							onChange={(e) => setMode(e.target.value as typeof mode)}
						>
							<option value="both">둘 다 두기 · 설정과 같은 찜은 유지</option>
							<option value="replace">바꾸기</option>
						</select>
					</label>
					<button
						type="button"
						className="button primary"
						disabled={busy}
						onClick={() =>
							void action(async () => {
								await restoreDeviceBackup(preview, mode);
								setPreview(null);
								return "백업의 기록을 복원했어요.";
							})
						}
					>
						확인한 백업 복원
					</button>
				</>
			)}
			<h3>저장 공간</h3>
			<button
				type="button"
				className="button secondary"
				onClick={async () => {
					try {
						const e = await navigator.storage?.estimate?.();
						setStorage(
							e?.usage !== undefined
								? `사용 중 약 ${(e.usage / 1024 / 1024).toFixed(1)}MB · 한도 약 ${((e.quota ?? 0) / 1024 / 1024).toFixed(0)}MB`
								: "이 브라우저에서는 용량을 확인할 수 없어요. 사진을 백업하고 줄일 수 있어요.",
						);
					} catch {
						setStorage("저장 공간을 확인하지 못했어요.");
					}
				}}
			>
				저장 공간 확인
			</button>
			<p role="status">{storage}</p>
			<p>
				사진을 줄이기 전에 사진 포함 전체 백업을 받아 두세요. 원본 대신 긴 변
				1280px 이하 사진을 기기에 남겨요.
			</p>
			<button
				type="button"
				className="button secondary"
				disabled={busy}
				onClick={() =>
					void action(async () => {
						const r = await shrinkTripPhotos();
						return `사진 ${r.count}장 줄임 · 약 ${Math.round((r.before - r.after) / 1024)}KB 확보`;
					})
				}
			>
				필름 사진 줄이기
			</button>
			<h3>이 기기 기록 전부 지우기</h3>
			<p>
				여행·사진·모집문·설정과 최근 지운 여행을 모두 지워요. 먼저 전체 백업을
				받아 두세요.
			</p>
			<label>
				확인 문구 ‘지우기’
				<input value={confirm} onChange={(e) => setConfirm(e.target.value)} />
			</label>
			<button
				type="button"
				className="button secondary"
				disabled={busy || confirm !== "지우기"}
				onClick={() =>
					void action(async () => {
						await clearDevice();
						setConfirm("");
						return "이 기기 기록을 모두 지웠어요.";
					})
				}
			>
				기기 기록 전부 지우기
			</button>
			<p role="status">{notice}</p>
		</details>
	);
}
